import React, { Component } from 'react';
import {
    Dialog, DialogContent, Typography, Button, Divider, IconButton, TextField, CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EmailIcon from '@material-ui/icons/Email';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

// Cor destrutiva padronizada (Material Red 700).
const DANGER_COLOR = '#d32f2f';

const styles = {
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px 12px',
        backgroundColor: '#fff5f5',
    },
    content: {
        padding: '16px 24px 24px',
    },
    warningBox: {
        backgroundColor: '#fff3e0',
        border: '1px solid #ffb74d',
        borderRadius: 6,
        padding: '10px 14px',
        marginBottom: 16,
        color: '#e65100',
        fontSize: 13,
        lineHeight: 1.5,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 8,
        padding: '12px 24px 16px',
        backgroundColor: '#f5f5f5',
        borderTop: '1px solid #e0e0e0',
    },
    cancelBtn: {
        textTransform: 'none',
        fontWeight: 500,
        padding: '8px 24px',
        color: '#616161',
    },
    confirmBtn: (disabled) => ({
        backgroundColor: disabled ? '#bdbdbd' : DANGER_COLOR,
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        padding: '8px 28px',
        borderRadius: 6,
        boxShadow: disabled ? 'none' : '0 2px 8px rgba(211,47,47,0.3)',
        cursor: disabled ? 'not-allowed' : 'pointer',
    }),
};

/**
 * Dialog de confirmação destrutiva que exige o usuário digitar o workspace
 * exatamente para habilitar o botão de confirmação. Usado em ações irreversíveis
 * como desprovisionamento de ambiente.
 *
 * Props:
 * - open, onClose, onConfirm(payload: { reason, confirmWorkspace, twoFactorCode? })
 * - workspace: string que o usuário deve digitar pra habilitar o confirm
 * - title, message, description
 * - warningText: mensagem de alerta laranja no topo (opcional)
 * - confirmLabel (default 'Desprovisionar')
 * - requireReason: se true, exige digitar também uma justificativa (default false)
 * - loading: bloqueia botão durante requisição
 *
 * Modo 2FA (proteção em camadas contra cliques acidentais):
 * - requireTwoFactor: bool — quando true, exige código 2FA enviado por email pra habilitar confirm
 * - onSendCode({ email }): callback pra disparar envio do código (controla state externo)
 * - sendingCode: bool — botão "Enviar" mostra spinner
 * - codeSentState: { sent: bool, errormessage?: string } — feedback visual abaixo do botão
 * - defaultEmail: email pré-preenchido do admin logado (opcional)
 */
class DangerConfirmDialog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            typedWorkspace: '',
            reason: '',
            twoFactorCode: '',
            email: props.defaultEmail || '',
        };
    }

    componentDidUpdate(prevProps) {
        // Atualiza email pré-preenchido quando defaultEmail muda (modal abre com prop nova).
        if (this.props.defaultEmail !== prevProps.defaultEmail && !this.state.email) {
            this.setState({ email: this.props.defaultEmail || '' });
        }
    }

    handleReset = () => {
        this.setState({ typedWorkspace: '', reason: '', twoFactorCode: '' });
    };

    handleClose = () => {
        this.handleReset();
        if (this.props.onClose) this.props.onClose();
    };

    handleSendCode = () => {
        if (this.props.onSendCode) {
            this.props.onSendCode({ email: this.state.email });
        }
    };

    handleConfirm = () => {
        const { typedWorkspace, reason, twoFactorCode } = this.state;
        if (this.props.onConfirm) {
            this.props.onConfirm({ reason, confirmWorkspace: typedWorkspace, twoFactorCode });
        }
    };

    isConfirmDisabled = () => {
        const { typedWorkspace, reason, twoFactorCode } = this.state;
        const { workspace, requireReason, loading, requireTwoFactor, codeSentState } = this.props;
        if (loading) return true;
        if (!workspace) return true;
        if (typedWorkspace !== workspace) return true;
        if (requireReason && !reason.trim()) return true;
        if (requireTwoFactor) {
            // Código precisa ter 6 dígitos E ter sido efetivamente enviado (sent === true)
            if (!codeSentState || !codeSentState.sent) return true;
            if (!/^\d{6}$/.test(twoFactorCode || '')) return true;
        }
        return false;
    };

    render() {
        const {
            open, title, message, description, warningText,
            workspace, confirmLabel, requireReason, loading,
            requireTwoFactor, sendingCode, codeSentState,
        } = this.props;

        const disabled = this.isConfirmDisabled();
        const codeSent = !!(codeSentState && codeSentState.sent);
        const codeError = codeSentState && codeSentState.sent === false ? codeSentState.errormessage : null;

        return (
            <Dialog
                open={open}
                onClose={this.handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}
            >
                <div style={styles.header}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ErrorOutlineIcon style={{ fontSize: 28, color: DANGER_COLOR, marginRight: 12 }} />
                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18, color: '#c62828' }}>
                            {title || 'Ação destrutiva'}
                        </Typography>
                    </div>
                    <IconButton size="small" onClick={this.handleClose} style={{ color: '#9e9e9e' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <Divider />

                <DialogContent style={styles.content}>
                    {warningText && (
                        <div style={styles.warningBox}>
                            <strong>Atenção:</strong> {warningText}
                        </div>
                    )}

                    {message && (
                        <Typography variant="body1" style={{ marginBottom: description ? 12 : 16, color: '#424242', lineHeight: 1.6 }}>
                            {message}
                        </Typography>
                    )}
                    {description && (
                        <Typography variant="body2" style={{ marginBottom: 16, color: '#757575', lineHeight: 1.5 }}>
                            {description}
                        </Typography>
                    )}

                    <Typography variant="body2" style={{ color: '#424242', marginBottom: 8, fontWeight: 500 }}>
                        Para confirmar, digite o workspace <strong style={{ color: DANGER_COLOR, fontFamily: 'monospace' }}>{workspace}</strong>:
                    </Typography>
                    <TextField
                        value={this.state.typedWorkspace}
                        onChange={(e) => this.setState({ typedWorkspace: e.target.value })}
                        placeholder={workspace}
                        variant="outlined"
                        size="small"
                        fullWidth
                        autoComplete="off"
                        disabled={loading}
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                        style={{ marginBottom: 16 }}
                    />

                    {requireReason && (
                        <>
                            <Typography variant="body2" style={{ color: '#424242', marginBottom: 8, fontWeight: 500 }}>
                                Motivo (obrigatório):
                            </Typography>
                            <TextField
                                value={this.state.reason}
                                onChange={(e) => this.setState({ reason: e.target.value })}
                                placeholder="Ex.: cliente solicitou encerramento, teste concluído, etc."
                                variant="outlined"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                disabled={loading}
                            />
                        </>
                    )}
                    {!requireReason && (
                        <>
                            <Typography variant="body2" style={{ color: '#424242', marginBottom: 8, fontWeight: 500 }}>
                                Motivo (opcional):
                            </Typography>
                            <TextField
                                value={this.state.reason}
                                onChange={(e) => this.setState({ reason: e.target.value })}
                                placeholder="Observação para registro"
                                variant="outlined"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                disabled={loading}
                            />
                        </>
                    )}

                    {requireTwoFactor && (
                        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" style={{ color: '#424242', marginBottom: 8, fontWeight: 500 }}>
                                Código de verificação 2FA (enviado por email):
                            </Typography>
                            <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 8 }}>
                                <EmailIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                                {this.state.email || 'email do admin logado'}
                            </Typography>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                                <TextField
                                    value={this.state.twoFactorCode}
                                    onChange={(e) => this.setState({ twoFactorCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                                    placeholder="000000"
                                    variant="outlined"
                                    size="small"
                                    autoComplete="off"
                                    disabled={loading || !codeSent}
                                    inputProps={{
                                        style: { fontFamily: 'monospace', fontSize: 16, letterSpacing: 4, textAlign: 'center' },
                                        maxLength: 6,
                                    }}
                                    style={{ flex: '0 0 140px' }}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={this.handleSendCode}
                                    disabled={loading || sendingCode}
                                    startIcon={sendingCode ? <CircularProgress size={14} /> : <EmailIcon fontSize="small" />}
                                    style={{ textTransform: 'none', height: 40 }}
                                >
                                    {codeSent ? 'Reenviar código' : 'Enviar código'}
                                </Button>
                            </div>
                            {codeSent && (
                                <Typography variant="caption" style={{ color: '#388e3c', display: 'block' }}>
                                    Código enviado. Confira o email e digite os 6 dígitos acima.
                                </Typography>
                            )}
                            {codeError && (
                                <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, backgroundColor: '#ffebee', borderLeft: '4px solid #b71c1c', color: '#b71c1c', fontSize: 12 }}>
                                    {codeError}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>

                <div style={styles.footer}>
                    <Button
                        onClick={this.handleClose}
                        style={styles.cancelBtn}
                        variant="outlined"
                        disabled={loading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={this.handleConfirm}
                        variant="contained"
                        disabled={disabled}
                        style={styles.confirmBtn(disabled)}
                    >
                        {loading ? 'Processando...' : (confirmLabel || 'Desprovisionar')}
                    </Button>
                </div>
            </Dialog>
        );
    }
}

export default DangerConfirmDialog;
